import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import api from '../services/api';
import {
  BanknotesIcon,
  EnvelopeIcon,
  ChatBubbleLeftIcon,
  DevicePhoneMobileIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ShieldCheckIcon,
  BellIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';

const fetchBankingConfig = () => api.get('/config/banking').then(res => res.data.data.banking);
const fetchGatewayConfig = () => api.get('/config/gateway').then(res => res.data.data.gateway);
const fetchEmailConfig = () => api.get('/config/email').then(res => res.data.data.email);
const fetchSMSConfig = () => api.get('/config/sms').then(res => res.data.data.sms);
const fetchWhatsAppConfig = () => api.get('/config/whatsapp').then(res => res.data.data.whatsapp);

export default function Configuration() {
  const [activeTab, setActiveTab] = useState('banking');
  const [isTesting, setIsTesting] = useState(false);
  const queryClient = useQueryClient();

  const { data: bankingConfig } = useQuery('config-banking', fetchBankingConfig);
  const { data: gatewayConfig } = useQuery('config-gateway', fetchGatewayConfig);
  const { data: emailConfig } = useQuery('config-email', fetchEmailConfig);
  const { data: smsConfig } = useQuery('config-sms', fetchSMSConfig);
  const { data: whatsappConfig } = useQuery('config-whatsapp', fetchWhatsAppConfig);

  // Users lookup for targeting individuals
  const { data: usersList } = useQuery('users-list', () => api.get('/users').then(res => res.data.data.users));

  const [notiForm, setNotiForm] = useState({
    title: '',
    message: '',
    type: 'info',
    target: 'all', // all, role, user
    targetUserId: '',
    targetRole: 'billing_officer'
  });

  const updateBanking = useMutation(
    (data) => api.put('/config/banking', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('config-banking');
        toast.success('Banking configuration updated');
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Update failed')
    }
  );

  const updateGateway = useMutation(
    (data) => api.put('/config/gateway', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('config-gateway');
        toast.success('Card Gateway configuration updated');
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Update failed')
    }
  );

  const updateEmail = useMutation(
    (data) => api.put('/config/email', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('config-email');
        toast.success('Email configuration updated');
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Update failed')
    }
  );

  const updateSMS = useMutation(
    (data) => api.put('/config/sms', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('config-sms');
        toast.success('SMS configuration updated');
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Update failed')
    }
  );

  const updateWhatsApp = useMutation(
    (data) => api.put('/config/whatsapp', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('config-whatsapp');
        toast.success('WhatsApp configuration updated');
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Update failed')
    }
  );

  const createSystemNoti = useMutation(
    (data) => api.post('/notifications/system', data),
    {
      onSuccess: () => {
        toast.success('In-App Notification broadcasted successfully');
        setNotiForm({
          title: '',
          message: '',
          type: 'info',
          target: 'all',
          targetUserId: '',
          targetRole: 'billing_officer'
        });
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Failed to dispatch notification');
      }
    }
  );

  const testConnection = useMutation(
    (type) => api.post(`/config/test/${type}`),
    {
      onMutate: () => setIsTesting(true),
      onSuccess: (res) => {
        if (res.data.data.success) {
          toast.success(res.data.data.message);
        } else {
          toast.error(res.data.data.message);
        }
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Test failed'),
      onSettled: () => setIsTesting(false)
    }
  );

  const handleNotiSubmit = (e) => {
    e.preventDefault();
    if (!notiForm.title || !notiForm.message) {
      toast.error('Title and message are required');
      return;
    }
    const payload = {
      title: notiForm.title,
      message: notiForm.message,
      type: notiForm.type
    };
    if (notiForm.target === 'user') {
      if (!notiForm.targetUserId) {
        toast.error('Please select a target user');
        return;
      }
      payload.targetUserId = notiForm.targetUserId;
    } else if (notiForm.target === 'role') {
      payload.targetRole = notiForm.targetRole;
    }
    createSystemNoti.mutate(payload);
  };

  const tabs = [
    { id: 'banking', name: 'Banking APIs', icon: BanknotesIcon },
    { id: 'gateway', name: 'Card Gateway', icon: CreditCardIcon },
    { id: 'email', name: 'Email', icon: EnvelopeIcon },
    { id: 'sms', name: 'SMS', icon: DevicePhoneMobileIcon },
    { id: 'whatsapp', name: 'WhatsApp', icon: ChatBubbleLeftIcon },
    { id: 'notifications', name: 'System Alerts', icon: BellIcon },
  ];

  const renderBankingForm = () => (
    <form onSubmit={(e) => {
      e.preventDefault();
      const formData = new FormData(e.target);
      updateBanking.mutate({
        ablApiBaseUrl: formData.get('ablApiBaseUrl'),
        ablApiVersion: formData.get('ablApiVersion'),
        ablClientId: formData.get('ablClientId'),
        ablClientSecret: formData.get('ablClientSecret'),
        ablWebhookSecret: formData.get('ablWebhookSecret'),
        ablTimeout: parseInt(formData.get('ablTimeout')),
        ablRetryAttempts: parseInt(formData.get('ablRetryAttempts')),
        ablRetryDelay: parseInt(formData.get('ablRetryDelay')),
        testMode: formData.get('testMode') === 'true'
      });
    }}>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">ABL API Base URL</label>
          <input type="url" name="ablApiBaseUrl" defaultValue={bankingConfig?.ABL_API_BASE_URL?.value} className="input-field mt-1" placeholder="https://api.abl.com" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">API Version</label>
          <input type="text" name="ablApiVersion" defaultValue={bankingConfig?.ABL_API_VERSION?.value} className="input-field mt-1" placeholder="v1" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Client ID</label>
          <input type="text" name="ablClientId" defaultValue={bankingConfig?.ABL_CLIENT_ID?.value} className="input-field mt-1" placeholder="your_client_id" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Client Secret</label>
          <input type="password" name="ablClientSecret" defaultValue={bankingConfig?.ABL_CLIENT_SECRET?.value} className="input-field mt-1" placeholder="••••••••" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Webhook Secret</label>
          <input type="password" name="ablWebhookSecret" defaultValue={bankingConfig?.ABL_WEBHOOK_SECRET?.value} className="input-field mt-1" placeholder="••••••••" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Timeout (ms)</label>
          <input type="number" name="ablTimeout" defaultValue={bankingConfig?.ABL_TIMEOUT?.value || 30000} className="input-field mt-1" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Retry Attempts</label>
          <input type="number" name="ablRetryAttempts" defaultValue={bankingConfig?.ABL_RETRY_ATTEMPTS?.value || 3} className="input-field mt-1" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Retry Delay (ms)</label>
          <input type="number" name="ablRetryDelay" defaultValue={bankingConfig?.ABL_RETRY_DELAY?.value || 1000} className="input-field mt-1" />
        </div>
        <div className="sm:col-span-2">
          <label className="flex items-center">
            <input type="checkbox" name="testMode" value="true" defaultChecked={bankingConfig?.TEST_MODE?.value === 'true'} className="h-4 w-4 text-indigo-600 border-slate-300 rounded" />
            <span className="ml-2 text-sm text-slate-700 dark:text-slate-300">Enable Test Mode (Sandbox Environment)</span>
          </label>
          <p className="mt-1 text-xs text-slate-500">When enabled, all banking API calls will use the test/sandbox environment</p>
        </div>
      </div>
      <div className="mt-6 flex gap-3">
        <button type="submit" className="btn-primary" disabled={updateBanking.isLoading}>
          {updateBanking.isLoading ? 'Saving...' : 'Save Banking Config'}
        </button>
        <button type="button" onClick={() => testConnection.mutate('banking')} className="btn-secondary" disabled={isTesting}>
          <ArrowPathIcon className={`h-4 w-4 mr-2 ${isTesting ? 'animate-spin' : ''}`} />
          Test Connection
        </button>
      </div>
    </form>
  );

  const renderGatewayForm = () => (
    <form onSubmit={(e) => {
      e.preventDefault();
      const formData = new FormData(e.target);
      updateGateway.mutate({
        gatewayProvider: formData.get('gatewayProvider'),
        gatewayMerchantId: formData.get('gatewayMerchantId'),
        gatewaySecretKey: formData.get('gatewaySecretKey'),
        gatewayWebhookSecret: formData.get('gatewayWebhookSecret'),
        gatewayBaseUrl: formData.get('gatewayBaseUrl'),
        testMode: formData.get('testMode') === 'true'
      });
    }} className="text-left font-sans animate-fade-in space-y-6">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Payment Gateway Provider</label>
          <select name="gatewayProvider" defaultValue={gatewayConfig?.GATEWAY_PROVIDER?.value || 'stripe'} className="input-field mt-1">
            <option value="stripe">Stripe</option>
            <option value="cybersource">Cybersource (Visa)</option>
            <option value="braintree">Braintree (PayPal)</option>
            <option value="easypay">EasyPay Gateway</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Merchant Account ID</label>
          <input type="text" name="gatewayMerchantId" defaultValue={gatewayConfig?.GATEWAY_MERCHANT_ID?.value} className="input-field mt-1" placeholder="e.g. acct_1Nz..." />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Private API Secret Key</label>
          <input type="password" name="gatewaySecretKey" defaultValue={gatewayConfig?.GATEWAY_SECRET_KEY?.value} className="input-field mt-1" placeholder="••••••••" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Webhook Handshake Secret</label>
          <input type="password" name="gatewayWebhookSecret" defaultValue={gatewayConfig?.GATEWAY_WEBHOOK_SECRET?.value} className="input-field mt-1" placeholder="••••••••" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Gateway API Base URL</label>
          <input type="url" name="gatewayBaseUrl" defaultValue={gatewayConfig?.GATEWAY_BASE_URL?.value || 'https://api.stripe.com'} className="input-field mt-1" placeholder="https://api.stripe.com" />
        </div>
        <div className="sm:col-span-2 flex flex-col gap-1">
          <label className="flex items-center">
            <input type="checkbox" name="testMode" value="true" defaultChecked={gatewayConfig?.GATEWAY_TEST_MODE?.value === 'true'} className="h-4 w-4 text-indigo-600 border-slate-300 rounded" />
            <span className="ml-2 text-sm text-slate-700 dark:text-slate-300">Enable Sandbox Mode (Test Mode)</span>
          </label>
          <p className="text-xs text-slate-500">When enabled, transactions will not hit live credit card networks and are simulated.</p>
        </div>
      </div>
      <div className="mt-6 flex gap-3">
        <button type="submit" className="btn-primary py-2 px-5 text-sm" disabled={updateGateway.isLoading}>
          {updateGateway.isLoading ? 'Saving...' : 'Save Gateway Config'}
        </button>
        <button type="button" onClick={() => testConnection.mutate('gateway')} className="btn-secondary" disabled={isTesting}>
          <ArrowPathIcon className={`h-4 w-4 mr-2 ${isTesting ? 'animate-spin' : ''}`} />
          Test Connection
        </button>
      </div>
    </form>
  );

  const renderEmailForm = () => (
    <form onSubmit={(e) => {
      e.preventDefault();
      const formData = new FormData(e.target);
      updateEmail.mutate({
        provider: formData.get('provider'),
        smtpHost: formData.get('smtpHost'),
        smtpPort: parseInt(formData.get('smtpPort')),
        smtpSecure: formData.get('smtpSecure') === 'true',
        smtpUser: formData.get('smtpUser'),
        smtpPass: formData.get('smtpPass'),
        fromName: formData.get('fromName'),
        fromAddress: formData.get('fromAddress'),
        testMode: formData.get('testMode') === 'true'
      });
    }}>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Provider</label>
          <select name="provider" defaultValue={emailConfig?.EMAIL_PROVIDER?.value} className="input-field mt-1">
            <option value="smtp">SMTP</option>
            <option value="sendgrid">SendGrid</option>
            <option value="mailgun">Mailgun</option>
            <option value="aws_ses">AWS SES</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">SMTP Host</label>
          <input type="text" name="smtpHost" defaultValue={emailConfig?.EMAIL_SMTP_HOST?.value} className="input-field mt-1" placeholder="smtp.gmail.com" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">SMTP Port</label>
          <input type="number" name="smtpPort" defaultValue={emailConfig?.EMAIL_SMTP_PORT?.value || 587} className="input-field mt-1" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">SMTP User</label>
          <input type="text" name="smtpUser" defaultValue={emailConfig?.EMAIL_SMTP_USER?.value} className="input-field mt-1" placeholder="user@example.com" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">SMTP Password</label>
          <input type="password" name="smtpPass" defaultValue={emailConfig?.EMAIL_SMTP_PASS?.value} className="input-field mt-1" placeholder="••••••••" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">From Name</label>
          <input type="text" name="fromName" defaultValue={emailConfig?.EMAIL_FROM_NAME?.value} className="input-field mt-1" placeholder="XpertPayments" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">From Address</label>
          <input type="email" name="fromAddress" defaultValue={emailConfig?.EMAIL_FROM_ADDRESS?.value} className="input-field mt-1" placeholder="noreply@xpertpayments.com" />
        </div>
        <div className="sm:col-span-2">
          <label className="flex items-center">
            <input type="checkbox" name="testMode" value="true" defaultChecked={emailConfig?.EMAIL_TEST_MODE?.value === 'true'} className="h-4 w-4 text-indigo-600 border-slate-300 rounded" />
            <span className="ml-2 text-sm text-slate-700 dark:text-slate-300">Enable Test Mode (Do not send actual emails)</span>
          </label>
        </div>
      </div>
      <div className="mt-6 flex gap-3">
        <button type="submit" className="btn-primary" disabled={updateEmail.isLoading}>
          {updateEmail.isLoading ? 'Saving...' : 'Save Email Config'}
        </button>
        <button type="button" onClick={() => testConnection.mutate('email')} className="btn-secondary" disabled={isTesting}>
          <ArrowPathIcon className={`h-4 w-4 mr-2 ${isTesting ? 'animate-spin' : ''}`} />
          Test Connection
        </button>
      </div>
    </form>
  );

  const renderSMSForm = () => (
    <form onSubmit={(e) => {
      e.preventDefault();
      const formData = new FormData(e.target);
      updateSMS.mutate({
        provider: formData.get('provider'),
        twilioSid: formData.get('twilioSid'),
        twilioAuthToken: formData.get('twilioAuthToken'),
        twilioPhoneNumber: formData.get('twilioPhoneNumber'),
        testMode: formData.get('testMode') === 'true'
      });
    }}>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Provider</label>
          <select name="provider" defaultValue={smsConfig?.SMS_PROVIDER?.value} className="input-field mt-1">
            <option value="twilio">Twilio</option>
            <option value="aws_sns">AWS SNS</option>
            <option value="messagebird">MessageBird</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Twilio SID</label>
          <input type="text" name="twilioSid" defaultValue={smsConfig?.SMS_TWILIO_SID?.value} className="input-field mt-1" placeholder="AC..." />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Twilio Auth Token</label>
          <input type="password" name="twilioAuthToken" defaultValue={smsConfig?.SMS_TWILIO_AUTH_TOKEN?.value} className="input-field mt-1" placeholder="••••••••" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Twilio Phone Number</label>
          <input type="text" name="twilioPhoneNumber" defaultValue={smsConfig?.SMS_TWILIO_PHONE_NUMBER?.value} className="input-field mt-1" placeholder="+1234567890" />
        </div>
        <div className="sm:col-span-2">
          <label className="flex items-center">
            <input type="checkbox" name="testMode" value="true" defaultChecked={smsConfig?.SMS_TEST_MODE?.value === 'true'} className="h-4 w-4 text-indigo-600 border-slate-300 rounded" />
            <span className="ml-2 text-sm text-slate-700 dark:text-slate-300">Enable Test Mode (Do not send actual SMS)</span>
          </label>
        </div>
      </div>
      <div className="mt-6 flex gap-3">
        <button type="submit" className="btn-primary" disabled={updateSMS.isLoading}>
          {updateSMS.isLoading ? 'Saving...' : 'Save SMS Config'}
        </button>
        <button type="button" onClick={() => testConnection.mutate('sms')} className="btn-secondary" disabled={isTesting}>
          <ArrowPathIcon className={`h-4 w-4 mr-2 ${isTesting ? 'animate-spin' : ''}`} />
          Test Connection
        </button>
      </div>
    </form>
  );

  const renderWhatsAppForm = () => (
    <form onSubmit={(e) => {
      e.preventDefault();
      const formData = new FormData(e.target);
      updateWhatsApp.mutate({
        provider: formData.get('provider'),
        twilioSid: formData.get('twilioSid'),
        twilioAuthToken: formData.get('twilioAuthToken'),
        twilioPhoneNumber: formData.get('twilioPhoneNumber'),
        testMode: formData.get('testMode') === 'true'
      });
    }}>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Provider</label>
          <select name="provider" defaultValue={whatsappConfig?.WHATSAPP_PROVIDER?.value} className="input-field mt-1">
            <option value="twilio">Twilio</option>
            <option value="messagebird">MessageBird</option>
            <option value="360dialog">360dialog</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Twilio SID</label>
          <input type="text" name="twilioSid" defaultValue={whatsappConfig?.WHATSAPP_TWILIO_SID?.value} className="input-field mt-1" placeholder="AC..." />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Twilio Auth Token</label>
          <input type="password" name="twilioAuthToken" defaultValue={whatsappConfig?.WHATSAPP_TWILIO_AUTH_TOKEN?.value} className="input-field mt-1" placeholder="••••••••" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Twilio WhatsApp Number</label>
          <input type="text" name="twilioPhoneNumber" defaultValue={whatsappConfig?.WHATSAPP_TWILIO_PHONE_NUMBER?.value} className="input-field mt-1" placeholder="+1234567890" />
        </div>
        <div className="sm:col-span-2">
          <label className="flex items-center">
            <input type="checkbox" name="testMode" value="true" defaultChecked={whatsappConfig?.WHATSAPP_TEST_MODE?.value === 'true'} className="h-4 w-4 text-indigo-600 border-slate-300 rounded" />
            <span className="ml-2 text-sm text-slate-700 dark:text-slate-300">Enable Test Mode (Do not send actual WhatsApp messages)</span>
          </label>
        </div>
      </div>
      <div className="mt-6 flex gap-3">
        <button type="submit" className="btn-primary" disabled={updateWhatsApp.isLoading}>
          {updateWhatsApp.isLoading ? 'Saving...' : 'Save WhatsApp Config'}
        </button>
        <button type="button" onClick={() => testConnection.mutate('whatsapp')} className="btn-secondary" disabled={isTesting}>
          <ArrowPathIcon className={`h-4 w-4 mr-2 ${isTesting ? 'animate-spin' : ''}`} />
          Test Connection
        </button>
      </div>
    </form>
  );

  const renderSystemNotificationsForm = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* Creation form */}
      <form onSubmit={handleNotiSubmit} className="md:col-span-2 space-y-4 border-r border-slate-200 dark:border-slate-800 pr-6 text-left">
        <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-2">Create Notification Alert</h3>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Alert Title <span className="text-red-500">*</span></label>
          <input
            type="text"
            value={notiForm.title}
            onChange={(e) => setNotiForm({ ...notiForm, title: e.target.value })}
            placeholder="e.g. Scheduled System Upgrade"
            className="input-field mt-1"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Alert Message <span className="text-red-500">*</span></label>
          <textarea
            value={notiForm.message}
            onChange={(e) => setNotiForm({ ...notiForm, message: e.target.value })}
            placeholder="Introduce details of the alert..."
            rows={4}
            className="input-field mt-1"
            required
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Alert Type</label>
            <select
              value={notiForm.type}
              onChange={(e) => setNotiForm({ ...notiForm, type: e.target.value })}
              className="input-field mt-1"
            >
              <option value="info">Info (Blue)</option>
              <option value="success">Success (Green)</option>
              <option value="warning">Warning (Yellow)</option>
              <option value="error">Error (Red)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Target Scope</label>
            <select
              value={notiForm.target}
              onChange={(e) => setNotiForm({ ...notiForm, target: e.target.value })}
              className="input-field mt-1"
            >
              <option value="all">Broadcast to All Users</option>
              <option value="role">Target Specific Role</option>
              <option value="user">Target Specific User</option>
            </select>
          </div>
        </div>

        {notiForm.target === 'role' && (
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Select Role</label>
            <select
              value={notiForm.targetRole}
              onChange={(e) => setNotiForm({ ...notiForm, targetRole: e.target.value })}
              className="input-field mt-1"
            >
              <option value="billing_officer">Billing Officer</option>
              <option value="approver">Approver</option>
              <option value="supervisor">Supervisor</option>
              <option value="reconciliation_officer">Reconciliation Officer</option>
              <option value="auditor">Auditor</option>
              <option value="it_support">IT Support</option>
              <option value="admin">Administrator</option>
            </select>
          </div>
        )}

        {notiForm.target === 'user' && (
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Select User</label>
            <select
              value={notiForm.targetUserId}
              onChange={(e) => setNotiForm({ ...notiForm, targetUserId: e.target.value })}
              className="input-field mt-1"
              required
            >
              <option value="">-- Choose User --</option>
              {usersList?.map(u => (
                <option key={u.id} value={u.id}>{u.username} ({u.role.replace('_', ' ')})</option>
              ))}
            </select>
          </div>
        )}

        <div className="pt-2">
          <button type="submit" className="btn-primary" disabled={createSystemNoti.isLoading}>
            {createSystemNoti.isLoading ? 'Dispatching...' : 'Dispatch Alert'}
          </button>
        </div>
      </form>

      {/* Info sidebar */}
      <div className="space-y-4 pl-2 text-left">
        <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200">Alert Precedence & Delivery</h4>
        <p className="text-xs text-slate-500 leading-relaxed">
          Notifications are received in real-time by users in their top-right navigation bar icon dropdown.
          <br /><br />
          <strong>Broadcast</strong> messages are shown to all active logged-in staff members immediately.
          <br />
          <strong>Role targeted</strong> notifications will only filter to users matching that selected role policy.
          <br />
          <strong>User targeted</strong> notifications will exclusively be visible to the designated recipient profile.
        </p>
      </div>
    </div>
  );

  const renderForm = () => {
    switch (activeTab) {
      case 'banking': return renderBankingForm();
      case 'gateway': return renderGatewayForm();
      case 'email': return renderEmailForm();
      case 'sms': return renderSMSForm();
      case 'whatsapp': return renderWhatsAppForm();
      case 'notifications': return renderSystemNotificationsForm();
      default: return null;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Configuration</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Manage API URLs, keys, and alerts</p>
      </div>

      <div className="card">
        <div className="border-b border-slate-200 dark:border-slate-800 overflow-x-auto scrollbar-none">
          <nav className="-mb-px flex space-x-8 min-w-max px-6" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${activeTab === tab.id
                  ? 'border-indigo-505 text-indigo-600 dark:text-indigo-400 dark:border-indigo-405'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300 dark:text-slate-400 dark:hover:text-white'
                  }`}
              >
                <tab.icon className="h-5 w-5 mr-2" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>
        <div className="card-body">
          {renderForm()}
        </div>
      </div>

      {/* Security Notice */}
      <div className="card bg-yellow-50 border-yellow-200 dark:bg-yellow-950/20 dark:border-yellow-900/30">
        <div className="card-body flex items-start">
          <ShieldCheckIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-500 mt-0.5 mr-3" />
          <div className="text-left">
            <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-400">Security Notice</h3>
            <p className="mt-1 text-sm text-yellow-750 dark:text-yellow-600/90">
              All sensitive configuration values are encrypted using AES-256-GCM and stored securely.
              Test mode allows you to verify configurations without affecting production data.
              Always test connections before enabling production mode.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
